import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { appointmentService, Appointment } from '@/services/appointmentService';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { fr, enUS, it } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, Calendar } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import RendezVousModal from '@/components/ui/RendezVousModal';

const AppointmentsPage: React.FC = () => {
  const { user } = useAuth();
  const { language, t, tl } = useLanguage();
  const dateLocale = language === 'fr' ? fr : language === 'it' ? it : enUS;
  const navigate = useNavigate();
  const [viewingAppointment, setViewingAppointment] = useState<Appointment | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isRendezVousModalOpen, setIsRendezVousModalOpen] = useState(false);
  const { toast } = useToast();

  const { data: appointments = [], isLoading, error } = useQuery({
    queryKey: ['user', 'appointments', user?.id],
    queryFn: () => user?.id ? appointmentService.getUserAppointments() : Promise.resolve([]),
    enabled: !!user?.id,
  });

  const getStatusBadge = (status: Appointment['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
          {t('appointments.pending')}
        </Badge>;
      case 'confirmed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          {t('appointments.confirmed')}
        </Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          {t('appointments.cancelled')}
        </Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPp', { locale: dateLocale });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  const handleViewAppointment = (appointment: Appointment) => {
    setViewingAppointment(appointment);
    setIsViewDialogOpen(true);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white text-black">
        <div className="container mx-auto py-12 px-4">
          <div className="text-center">
            <p className="mb-4">
              {t('appointments.pleaseSignIn')}
            </p>
            <Button onClick={() => navigate('/auth')}>
              {t('appointments.signIn')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white text-black">
      <Helmet>
        <title>{t('appointments.title')} | Meubles Karim</title>
        <meta name="description" content={tl('Gérez vos rendez-vous pour la consultation de meubles', 'Manage your furniture consultation appointments', 'Gestisci i tuoi appuntamenti per la consulenza mobili')} />
      </Helmet>
      
      <div className="container mx-auto py-12 px-4">
        <h1 className="font-sans text-3xl font-medium mb-8 text-center">
          {t('appointments.title')}
        </h1>
        
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('appointments.appointments')}
              </CardTitle>
              <CardDescription>
                {t('appointments.manageDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" role="status">
                    <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">{tl('Chargement...', 'Loading...', 'Caricamento...')}</span>
                  </div>
                  <p className="mt-4 text-muted-foreground">
                    {t('appointments.loading')}
                  </p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  <p>
                    {t('appointments.error')}
                  </p>
                </div>
              ) : appointments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    {t('appointments.noAppointments')}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {t('appointments.noAppointmentsDescription')}
                  </p>
                  <Button variant="luxury" onClick={() => setIsRendezVousModalOpen(true)}>
                    {t('appointments.scheduleAppointment')}
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('appointments.date')}</TableHead>
                        <TableHead>{t('appointments.time')}</TableHead>
                        <TableHead>{t('appointments.status')}</TableHead>
                        <TableHead className="text-right">{t('appointments.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appointments.map((appointment) => (
                        <TableRow key={appointment.id}>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(appointment.appointment_date), 'PP', { locale: dateLocale })}
                          </TableCell>
                          <TableCell>{appointment.appointment_time}</TableCell>
                          <TableCell>{getStatusBadge(appointment.status)}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleViewAppointment(appointment)}
                            >
                              <Eye size={16} className="mr-2" />
                              {t('appointments.view')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl bg-white text-black">
          <DialogHeader>
            <DialogTitle>
              {t('appointments.appointmentDetails')}
            </DialogTitle>
          </DialogHeader>
          
          {viewingAppointment && (
            <div className="mt-4 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">
                      {t('appointments.appointmentId')}
                    </h3>
                    <p className="font-mono">#{viewingAppointment.id}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">
                      {t('appointments.status')}
                    </h3>
                    <div>{getStatusBadge(viewingAppointment.status)}</div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">
                      {t('appointments.created')}
                    </h3>
                    <p>{formatDate(viewingAppointment.created_at)}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">
                      {t('appointments.appointmentDate')}
                    </h3>
                    <p>
                      {format(new Date(viewingAppointment.appointment_date), 'PP', { locale: dateLocale })}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">
                      {t('appointments.appointmentTime')}
                    </h3>
                    <p>{viewingAppointment.appointment_time}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* RendezVous Modal */}
      <RendezVousModal 
        open={isRendezVousModalOpen}
        onOpenChange={setIsRendezVousModalOpen}
        user={user}
        language={language}
      />
    </div>
  );
};

export default AppointmentsPage; 